import type { AppProps } from 'next/app';
import { I18nextProvider } from 'react-i18next';
import Script from 'next/script';
import { ApolloProvider, NormalizedCacheObject } from '@apollo/client';
import { loadDevMessages } from '@apollo/client/dev';
import { ClientWrapper } from '@/components/ClientWrapper';
import { GlobalStyles } from '@/styles/globalStyles';
import i18n from '@/i18n';
import { BlockerDetector } from '@/components/Banners/BlockerDetector';
import NoSsr from '@/components/NoSsr/NoSsr';
import { BannersStyles } from '@/components/Banners/Banners.styles';
import { createApolloClient } from '@/config/apollo-client';
import { isProduction } from '@/utils/helpers';
import { PubNubInit } from '@/components/PubNubInit/PubNubInit';
import { AuthCheck } from '@/components/PubNubInit/AuthCheck';
import { BridgeProvider } from '@/bridge/BridgeProvider';
import { ThemeProviderWrapper } from '@/hooks/theme/wrapper';
import { Seo } from '@/components/Seo/Seo';
import { SeoFullData } from '@/api/types/seo.types';

if (!isProduction()) {
  loadDevMessages();
}

function App({
  Component,
  pageProps,
}: AppProps<{
  isSSRWebView: boolean;
  seoFullData: SeoFullData;
  apolloState?: NormalizedCacheObject | null;
}>) {
  const apolloClient = createApolloClient(i18n.language, pageProps.apolloState);

  return (
    <>
      <Script src="/scripts/adriver.core.2.js" strategy="lazyOnload" />
      <Seo {...pageProps.seoFullData} />
      <BridgeProvider isWebView={pageProps.isSSRWebView}>
        <ThemeProviderWrapper isWebView={pageProps.isSSRWebView}>
          <GlobalStyles />
          <BannersStyles />
          <I18nextProvider i18n={i18n}>
            <ApolloProvider client={apolloClient}>
              <ClientWrapper isSSRWebView={pageProps.isSSRWebView}>
                <AuthCheck />
                <PubNubInit />
                <NoSsr>
                  <BlockerDetector />
                </NoSsr>
                <Component {...pageProps} />
              </ClientWrapper>
            </ApolloProvider>
          </I18nextProvider>
        </ThemeProviderWrapper>
      </BridgeProvider>
    </>
  );
}

export default App;
